import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';

import useChangeLanguage from 'hooks/useChangeLanguage';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import { version as desktopVersion } from '../../../../../apps/desktop/package.json';
import * as S from './Layout.styled';
import RegisterSlide from './RegisterSlide';

const Layout = ({ children }) => {
	const localization = useSelector((state) => state.layout.localization);
	const { state, pathname } = useLocation();

	const [languageMenuButtonRef, setLanguageMenuButtonRef] = useState(null);
	const [versionClick, setVersionClick] = useState(0);
	const changeLanguage = useChangeLanguage();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const isSecondStep = useSelector((state) => state.register.isSecondStep);

	const showParachuteBox = isSecondStep || pathname.split('/')[1] === 'register-with-provider';

	const isTablet = useMediaQuery(Breakpoints.tablet);
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const checkShowSlideShow = useCallback(
		() =>
			!isTablet &&
			[
				PATHS.LOGIN,
				PATHS.REGISTER,
				PATHS.REGISTER_WITH_PROVIDER,
				PATHS.FORGOT_PASSWORD,
				PATHS.RESET_PASSWORD
			].includes(`/${pathname.split('/')[1]}`),
		[pathname, isTablet]
	);
	const [isSlideShow, setIsSlideShow] = useState(checkShowSlideShow());

	useUpdateEffect(() => {
		setIsSlideShow(checkShowSlideShow());
	}, [pathname, isTablet]);

	const referralToken = searchParams?.get('referralToken');

	const handleLanguageChange = useCallback(
		async (language) => {
			await changeLanguage(language.code);
			if (pathname.split('/')[1] === 'register') {
				navigate(
					{
						pathname: `${PATHS.REGISTER}/${language.systemCode}`,
						search: searchParams.toString()
					},
					{
						state: { redirectTo: state?.redirectTo }
					}
				);
			} else if (pathname.split('/')[1] === 'login') {
				navigate(
					{
						pathname: `${PATHS.LOGIN}/${language.systemCode}`,
						search: searchParams.toString()
					},
					{
						state: { redirectTo: state?.redirectTo }
					}
				);
			} else if (pathname.split('/')[1] === 'change-password') {
				navigate(
					{
						pathname: `${PATHS.RESET_PASSWORD}/${language.systemCode}`,
						search: searchParams.toString()
					},
					{
						state: { redirectTo: state?.redirectTo }
					}
				);
			}
		},
		[changeLanguage, pathname, searchParams, state, navigate]
	);

	const handleClickDesktopVersion = () => {
		setVersionClick((prev) => prev + 1);
		if (versionClick === 9) {
			setVersionClick(0);
			window.electronAPI.openDevTools();
		}
	};

	return (
		<S.Container>
			<S.ContentContainer isSlideShow={isSlideShow} isMobile={isMobile}>
				<S.LogoContainer style={{ zIndex: 1 }}>
					<Image
						width={'6.5rem'}
						height={'6.5rem'}
						url="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
					/>

					<div>
						<Button variant="borderless" ref={setLanguageMenuButtonRef}>
							<Image url={localization.icon} width="20px" height="15px" />
							<Typography>{localization.name}</Typography>
							<Icon icon={faAngleDown} />
						</Button>
						{languageMenuButtonRef && (
							<Menu
								buttonRef={languageMenuButtonRef}
								menuItems={languages.map((language) => ({
									label: language.name,
									img: language.icon,
									imgProps: { width: '20px', height: '15px' },
									selected: language.code === localization.code,
									action: () => handleLanguageChange(language)
								}))}
							/>
						)}
					</div>
				</S.LogoContainer>
				<S.Content style={{ zIndex: 1 }}>{children}</S.Content>

				<S.Footer>
					<S.FooterContainer>
						<Typography className="footer-company-title" color="var(--content-3)">
							OTO Global Inc. © 2025
						</Typography>
						<S.MailLink href="mailto:support@tryoto.com">
							<Typography color="var(--blue)">support@tryoto.com</Typography>
						</S.MailLink>
						<Typography
							color="var(--content-3)"
							{...(import.meta.env.VITE_PLATFORM === 'desktop'
								? { onClick: handleClickDesktopVersion }
								: {})}>
							App Version v{' '}
							{import.meta.env.VITE_PLATFORM === 'desktop'
								? desktopVersion
								: import.meta.env.VITE_APP_VERSION}
						</Typography>
					</S.FooterContainer>
				</S.Footer>
			</S.ContentContainer>

			{isSlideShow && !showParachuteBox && (
				<RegisterSlide isReferral={!!referralToken} isSlideShow={isSlideShow} setIsSlideShow={setIsSlideShow} />
			)}
			{isSlideShow && showParachuteBox && (
				<Image
					url="https://storage.googleapis.com/tryoto-public/public/assets/images/parachute_box_with_bg.svg"
					style={{ position: 'absolute', rigth: 0, bottom: 0, zIndex: 0 }}
					width="100%"
					height="auto"
				/>
			)}
		</S.Container>
	);
};

export default Layout;
